import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { SiteFooter } from '../components/Layout';
import { Modal, TextInput, useToast } from '../components/ui';

/**
 * Asking for a reset link.
 *
 * The confirmation is deliberately the same whether or not the address is
 * registered — the server answers identically, and saying anything more here
 * would undo that.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: () => api.post('/api/auth/forgot-password', { email: email.trim() }),
    onSuccess: () => setSent(true),
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? caught.message : 'Could not send that just now. Try again.',
      ),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    ask.mutate();
  };

  return (
    <div className="auth-shell">
      <aside className="auth-aside">
        <div className="row gap-6">
          <div className="brand-mark">K</div>
          <div className="brand-name" style={{ fontSize: 17 }}>
            Kortex HMS
          </div>
        </div>
        <div>
          <h1>Locked out?</h1>
          <p>
            We will send a link to the address you sign in with. It works once and expires in half
            an hour.
          </p>
        </div>
        <div className="small" style={{ color: '#7fa9a4' }}>
          Remembered it?{' '}
          <Link to="/sign-in" style={{ color: '#cfe3e0' }}>
            Sign in
          </Link>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          <h2>Reset your password</h2>
          <div className="sub">Enter the email address on your account.</div>

          {sent ? (
            <div className="col gap-16">
              <div className="auth-hint">
                If <b>{email.trim()}</b> belongs to an account, a reset link is on its way. Check
                your inbox, and your spam folder.
              </div>
              <p className="small muted">
                Nothing arrived? Your hotel may not have email set up yet — ask whoever administers
                Kortex to reset it for you from Users &amp; Roles.
              </p>
              <Link className="btn primary block center" to="/sign-in">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="col gap-16">
              {error ? <div className="form-error">{error}</div> : null}
              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                required
                placeholder="you@property.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                className="btn primary lg block"
                type="submit"
                disabled={!email.trim() || ask.isPending}
              >
                {ask.isPending ? 'Sending…' : 'Send me a link'}
              </button>
              <div className="center small muted">
                <Link to="/sign-in">Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
        <SiteFooter />
      </main>
    </div>
  );
}

/** Setting a new password from the emailed link. */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = useMutation({
    mutationFn: () => api.post('/api/auth/reset-password', { token, newPassword: password }),
    onSuccess: () => setDone(true),
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? caught.message : 'Could not reset it. Ask for a new link.',
      ),
  });

  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (mismatch) return;
    reset.mutate();
  };

  return (
    <div className="auth-shell">
      <aside className="auth-aside">
        <div className="row gap-6">
          <div className="brand-mark">K</div>
          <div className="brand-name" style={{ fontSize: 17 }}>
            Kortex HMS
          </div>
        </div>
        <div>
          <h1>Choose a new password</h1>
          <p>
            Setting it signs out every device that was signed in — including whoever you were
            locked out by.
          </p>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          {!token ? (
            <>
              <h2>That link is incomplete</h2>
              <div className="sub">Open the link from your email, or ask for a new one.</div>
              <Link className="btn primary block center" to="/forgot-password">
                Ask for a new link
              </Link>
            </>
          ) : done ? (
            <>
              <h2>Password changed</h2>
              <div className="sub">Sign in with your new password.</div>
              <button className="btn primary lg block" onClick={() => navigate('/sign-in')}>
                Go to sign in
              </button>
            </>
          ) : (
            <>
              <h2>Set a new password</h2>
              <div className="sub">This link works once.</div>
              <form onSubmit={submit} className="col gap-16">
                {error ? <div className="form-error">{error}</div> : null}
                <TextInput
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                  required
                  hint="At least 12 characters, with upper case, lower case and a number."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <TextInput
                  label="Confirm it"
                  type="password"
                  autoComplete="new-password"
                  required
                  error={mismatch ? 'Those do not match' : undefined}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                <button
                  className="btn primary lg block"
                  type="submit"
                  disabled={!password || mismatch || reset.isPending}
                >
                  {reset.isPending ? 'Saving…' : 'Set my password'}
                </button>
              </form>
            </>
          )}
        </div>
        <SiteFooter />
      </main>
    </div>
  );
}

/** Changing your own password while signed in. */
export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const { push, fail } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const change = useMutation({
    mutationFn: () =>
      api.post('/api/auth/change-password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      push('Password changed — other devices signed out', 'ok');
      onClose();
    },
    onError: fail,
  });

  const mismatch = confirm.length > 0 && confirm !== next;
  const ready = current.length > 0 && next.length > 0 && !mismatch && confirm.length > 0;

  return (
    <Modal
      title="Change your password"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!ready || change.isPending}
            onClick={() => change.mutate()}
          >
            {change.isPending ? 'Saving…' : 'Change it'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <TextInput
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <TextInput
          label="New password"
          type="password"
          autoComplete="new-password"
          hint="At least 12 characters, with upper case, lower case and a number."
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <TextInput
          label="Confirm it"
          type="password"
          autoComplete="new-password"
          error={mismatch ? 'Those do not match' : undefined}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <p className="small muted">
          Changing your password signs you out everywhere else. This device stays signed in.
        </p>
      </div>
    </Modal>
  );
}
